import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import { DisabledCampaignState } from '@/campaigns/DisabledCampaignState';
import { PersonalCampaignCard } from '@/campaigns/PersonalCampaignCard';
import { usePersonalCampaignAccess } from '@/campaigns/usePersonalCampaignAccess';
import { AlertBox } from '@/common/AlertBox';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { CampaignStudentBoardTable } from '@/tables/CampaignStudentBoardTable';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/campaigns',
)({
  component: CampaignsScreen,
});

/**
 * The study helper group's Campaigns tab.
 *
 * With no active campaign it shows the Enable empty state, whose button creates
 * one; otherwise the share card over the helper's own board. As in legacy, the
 * card is group-scoped and the board caller-scoped, so a helper viewing a
 * campaign someone else owns sees a populated card over an empty board.
 *
 * Legacy hides the tab rather than returning 403, so a refused caller is
 * redirected to the group dashboard. `isResolved` matters: `getGroupBySlug` is
 * pending on first paint, and a bare negative would bounce the group's own
 * owner.
 */
function CampaignsScreen(): ReactElement | null {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const { hasAccess, isResolved, groupId } = usePersonalCampaignAccess();
  const refused = isResolved && !hasAccess;

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  const { data: state } = api.getPersonalCampaign.useQuery(
    { path: { group_id: groupId ?? 0 } },
    { enabled: groupId !== undefined && hasAccess },
  );

  const {
    mutateAsync: enableCampaign,
    isPending: isEnabling,
    error: enableError,
  } = api.enablePersonalCampaign.useMutation();

  const handleEnableCampaign = async (): Promise<void> => {
    if (groupId === undefined) return;
    try {
      await enableCampaign({ path: { group_id: groupId } });
      // The POST answers with the campaign, but the card reads the GET, and the
      // board under it only exists once a campaign does.
      await api.getPersonalCampaign.invalidateQueries();
    } catch {
      // Rendered from `enableError` below. The only code this can raise is the
      // 409 `duplicate_subdomain`, when two Enable clicks race to the same
      // derived subdomain.
    }
  };

  if (refused) return null;

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.personal-campaign')}
          placement="study_helper_personal_outreach"
        />
      </GroupHeaderWrapper>
      {enableError && (
        <AlertBox variant="error">
          {errorMessage(enableError, t('wbs.something-went-wrong-please-2'))}
        </AlertBox>
      )}
      {/* Nothing until the read lands: `campaign: null` and "not fetched
         yet" look the same, and showing Enable over an enabled campaign
         invites a second create. */}
      {state !== undefined &&
        (state.campaign ? (
          <>
            <PersonalCampaignCard campaign={state.campaign} />
            <CampaignStudentBoardTable groupId={groupId} />
          </>
        ) : (
          <DisabledCampaignState
            isEnabling={isEnabling}
            onEnable={() => {
              void handleEnableCampaign();
            }}
          />
        ))}
    </>
  );
}
